export class ModalInitializer {
	// Set while the overlay slides in, so further clicks do not start a second navigation
	private navigationPending = false;

	public init = () => {
		const modalLinks: NodeListOf<HTMLAnchorElement> = document.querySelectorAll('.modallink');

		modalLinks.forEach((link) => {
			link.addEventListener('click', (event: MouseEvent) => {
				if (event.defaultPrevented) return;
				// Only a plain click navigates this tab; ctrl/cmd/shift clicks or target=_blank
				// open a new tab and would leave the overlay behind on this page.
				if (!ModalInitializer.navigatesInThisTab(link, event)) return;

				// Modal pages come from the static file cache and load faster than the overlay
				// slides in, so the browser would replace the page before the overlay is visible.
				// The navigation therefore starts once the open animation has finished.
				event.preventDefault();
				if (this.navigationPending) return;
				this.navigationPending = true;

				const overlay = this.createAndShowModal();
				ModalInitializer.afterOpenAnimation(overlay, () => this.navigateTo(link.href));
			});
		});

		// The overlay only signals "loading" while the browser navigates away. When the user
		// returns via history.back() the page is restored from the back/forward cache with
		// the overlay still in the DOM, so remove it before the page is cached and again
		// when it is shown from the cache.
		window.addEventListener('pagehide', this.reset);
		window.addEventListener('pageshow', (event: PageTransitionEvent) => {
			if (event.persisted) this.reset();
		});
	};

	public static navigatesInThisTab(link: HTMLAnchorElement, event: MouseEvent): boolean {
		return (
			event.button === 0 &&
			!event.ctrlKey &&
			!event.metaKey &&
			!event.shiftKey &&
			!event.altKey &&
			link.target !== '_blank'
		);
	}

	public static removeModal(): void {
		document.querySelectorAll('.modal-overlay').forEach((overlay) => overlay.remove());
	}

	/**
	 * Calls back when the open animation of the overlay has finished, right away without an
	 * animation (e.g. prefers-reduced-motion).
	 */
	public static afterOpenAnimation(overlay: HTMLElement, callback: () => void): void {
		const durations = getComputedStyle(overlay)
			.animationDuration.split(',')
			.map((duration) => parseFloat(duration))
			.filter((duration) => Number.isFinite(duration));
		const durationMs = Math.max(0, ...durations) * 1000;
		if (durationMs === 0) {
			callback();
			return;
		}

		let finished = false;
		const finish = () => {
			if (finished) return;
			finished = true;
			overlay.removeEventListener('animationend', onAnimationEnd);
			clearTimeout(fallback);
			callback();
		};
		const onAnimationEnd = (event: Event) => {
			// The spinner inside runs its own animation
			if (event.target === overlay) finish();
		};
		overlay.addEventListener('animationend', onAnimationEnd);
		// animationend does not fire in background tabs or when the animation is cancelled
		const fallback = setTimeout(finish, durationMs + 100);
	}

	public navigateTo(url: string): void {
		window.location.assign(url);
	}

	public createAndShowModal(): HTMLElement {
		const modal = document.createElement('div');
		modal.className = 'modal-overlay';

		const spinner = document.createElement('div');
		spinner.className = 'loading-spinner';

		modal.appendChild(spinner);

		document.body.appendChild(modal);

		return modal;
	}

	private reset = (): void => {
		this.navigationPending = false;
		ModalInitializer.removeModal();
	};
}
